import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'

import { DocumentTabPane } from '.'
import {
  closeUploadModal,
  openUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import { UploadDocumentType } from '@/constants/businessConstants'

import { markUserActionItemCompleteIfExists } from '@/features/user/dashboard/lib/service'
import { UserActionItemActionItemIdentifiers } from '@/features/user/dashboard/user-action-items/lib/userActionItems.slice'
import { GettingStartedOnboardingStep } from '@/features/onboarding/user-onboarding-steps/onboarding.reducer'
import { useAnalyticsTrack } from '@/features/amplitude'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { useAppDispatch } from '@/utils/typeHelpers'
import { fetchUserDocuments } from '../lib/userDocuments.slice'

import { MissingStatements } from '../shared/MissingStatements'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'
import {
  BankStatementUploadList,
  type BankStatementUploadListHandle,
} from '../shared/BankStatementUploadList'
import { RowTypes } from '../table/TableRows'
import { UserDocumentsTable } from '../table'
import { TabCategoryKeys as ModalTypes } from '../lib/constants'
import { Link, Text, DocumentUploadDialog } from '@/ui'

type BankStatementsUrlParams = {
  tabName?: string
  action?: string
}

const COLUMNS = [
  {
    sortBy: 'createdAt',
    title: 'Date Uploaded',
  },
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'tag',
    title: 'Statement Month',
  },
  {
    sortBy: 'financialAccount',
    title: 'Financial Account',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const BankStatements: DocumentTabPane = ({
  documents,
  tableOptions,
}) => {
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [monthQuery, setMonthQuery] = useState<number>()
  const [yearQuery, setYearQuery] = useState<number>()
  const [extraPayload, setExtraPayload] = useState<
    Record<string, string | number | (string | number)[]>
  >({})
  const [searchParams] = useSearchParams()
  const { activeUploadModal, selectedDocument, isRefreshingAfterDelete } =
    useFeatureState()
  const { tabName, action } = useParams<BankStatementsUrlParams>()
  const location = useLocation()
  const locationState = location.state
  const track = useAnalyticsTrack()
  const dispatch = useFeatureDispatch()
  const appDispatch = useAppDispatch()
  const uploadListRef = useRef<BankStatementUploadListHandle>(null)

  useEffect(() => {
    appDispatch(fetchFinancialAccountsIfNeeded())
  }, [appDispatch])

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const openModal = useCallback(() => {
    track('clicked document upload', {
      document_category: ModalTypes.BANK_STATEMENTS,
      document_subcategory: '',
    })

    dispatch?.(openUploadModal(ModalTypes.BANK_STATEMENTS))
  }, [dispatch, track])

  useEffect(() => {
    if (
      locationState?.initiatedBy ===
      GettingStartedOnboardingStep.uploadBankStatements
    ) {
      openModal()
    }
  }, [locationState?.initiatedBy, openModal])

  useEffect(() => {
    if (!isRefreshing && !isRefreshingAfterDelete) {
      setFilteredDocuments(documents)
    } else if (isRefreshingAfterDelete) {
      // Clear table when deleting
      setFilteredDocuments([])
    }

    if (tabName) {
      const month = Number(searchParams.get('month'))
      const year = Number(searchParams.get('year'))

      if (action === 'upload' && !isNaN(month) && !isNaN(year)) {
        setMonthQuery(month)
        setYearQuery(year)
        openModal()
      }
    }
  }, [
    documents,
    action,
    tabName,
    location.search,
    openModal,
    searchParams,
    isRefreshing,
    isRefreshingAfterDelete,
  ])

  const setUploadedFile = useCallback(
    async ({ statementMonth }: { statementMonth?: DateTime }) => {
      if (statementMonth) {
        await markUserActionItemCompleteIfExists(
          UserActionItemActionItemIdentifiers.uploadBankStatements({
            month: statementMonth.month.toString(),
            year: statementMonth.year.toString(),
          }),
          (event, properties) => track(event, properties)
        )
      }
    },
    [track]
  )

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: 'bank_statements',
    documentType: UploadDocumentType.STATEMENT,
    extraPayload: selectedDocument?.userDocumentCategoryId
      ? {
          ...extraPayload,
          userDocumentCategoryId: selectedDocument.userDocumentCategoryId,
        }
      : extraPayload,
    month: monthQuery,
    year: yearQuery?.toString(),
    onUploadSuccess: async (_uploadedDocuments) => {
      const statementMonth =
        monthQuery && yearQuery
          ? DateTime.fromObject(
              { month: monthQuery, year: yearQuery },
              { zone: 'utc' }
            )
          : undefined

      if (statementMonth) {
        await setUploadedFile({ statementMonth })
      }

      // Clear the table immediately
      setIsRefreshing(true)
      setFilteredDocuments([])

      // Close the modal and clear extra payload
      setExtraPayload({})
      dispatch?.(closeUploadModal())

      // Fetch fresh data
      await appDispatch(fetchUserDocuments(true, true))

      // Re-enable updates from props
      setIsRefreshing(false)
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
  })

  const handleDocumentsUpload = useCallback(
    async (documents: File[]) => {
      await uploadDocuments(documents)
    },
    [uploadDocuments]
  )

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        // TODO: Update to V2 File Modal
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <MissingStatements includeUploadButton={false} />

        <Text>
          Share monthly bank and credit card statements here.{' '}
          <Link
            href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
            newPage
          >
            When do I need to upload statements?
          </Link>
        </Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.BANK_STATEMENT}
        tableOptions={tableOptions}
      />

      <DocumentUploadDialog
        isOpen={activeUploadModal === ModalTypes.BANK_STATEMENTS}
        onOpenChange={(open) => {
          if (!open) {
            dispatch?.(closeUploadModal())
          }
        }}
        onClose={() => dispatch?.(closeUploadModal())}
        onUpload={handleDocumentsUpload}
        onValidateBeforeUpload={() => uploadListRef.current?.validate() ?? true}
        isUploadDisabled={isUploading}
        cardTitle="Bank Statements"
        cardDescription={
          <>
            Please upload the bank statements below so we can complete your
            bookkeeping.{' '}
            <Link
              href="https://support.joinheard.com/hc/en-us/articles/23569956149399-Bank-Statements-at-Heard"
              newPage
            >
              Why?
            </Link>
          </>
        }
      >
        <DocumentUploadDialog.CardContent>
          <MissingStatements accordionOnly />
        </DocumentUploadDialog.CardContent>

        <DocumentUploadDialog.FileListContent>
          <BankStatementUploadList
            ref={uploadListRef}
            setExtraPayload={setExtraPayload}
          />
        </DocumentUploadDialog.FileListContent>
      </DocumentUploadDialog>
    </>
  )
}

BankStatements.paneDescription =
  'Please use this section to upload your monthly business bank account and business credit card statements. We use these to reconcile your books each month and ensure that all of your income and expenses are accounted for. If you have granted our team read-only access to your bank account(s), you do not need to upload statements.'
BankStatements.paneTitle = 'Bank Statement'
